import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DetailsPanelShellComponent } from 'src/app/components/details-panel-shell/details-panel-shell.component';
import { StatusPillComponent } from 'src/app/components/status-pill/status-pill.component';
import { PriorityPillComponent } from 'src/app/components/priority-pill/priority-pill.component';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { TaskStatusConfig } from 'src/app/constants/status-pill-configs';
import { CommentService } from 'src/app/services/comment.service';
import { ConfigService } from 'src/app/services/config.service';
import { DataAccessService } from 'src/app/services/data-access.service';
import { AppGlobals } from 'src/app/app.globals';
import { CalendarEntry } from '../../../../../../components/calendar-month-panel/calendar.types';

// Ticket #4549: side panel shown when a task (Date-type result) entry is clicked on the
// project calendar. Comments are fetched read-only via the same generic CommentService the
// full task grid uses (CommentEditorComponent) — deliberately not embedding that heavier,
// editable component here since the panel only needs to *show* comments, not manage them.
@Component({
    selector: 'app-task-detail-panel',
    imports: [CommonModule, DetailsPanelShellComponent, StatusPillComponent, PriorityPillComponent, GpdButtonComponent, SvgIconComponent],
    templateUrl: './task-detail-panel.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../../../../../../components/details-panel-shell/panel-content.css', './task-detail-panel.component.css']
})
export class TaskDetailPanelComponent implements OnChanges {
  @Input() visible = false;
  @Input() entry: CalendarEntry | null = null;
  @Input() projectId: number | null = null;
  @Input() siteId: number | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onOpenTask = new EventEmitter<void>();

  readonly taskStatusConfig = TaskStatusConfig;

  comments: any[] = [];
  loadingComments = false;

  readonly labelName = $localize`:@@name:Name`;
  readonly labelTaskDetails = $localize`:@@taskDetailsSection:Task details`;
  readonly labelAssignedTo = $localize`:@@assignedTo:Assigned to`;
  readonly labelPriority = $localize`:@@priority:Priority`;
  readonly labelStatus = $localize`:@@status:Status`;
  readonly labelComments = $localize`:@@comments:Comments`;
  readonly labelNoComments = $localize`:@@calendarNoComments:No comments yet.`;
  readonly labelOpenTask = $localize`:@@calendarOpenTask:Open task`;

  private userMap: { [email: string]: string } = {};
  private departments: any[] = [];

  constructor(
    private commentService: CommentService,
    private configService: ConfigService,
    private dataAccessService: DataAccessService,
  ) {
    this.configService.loadUserList();
    this.configService.userMap$.subscribe((map) => (this.userMap = map));
    // Cached (loadDepartments() only hits the API once, then just replays the same list) —
    // used as the "Assigned to" placeholder when a task has no specific user assigned yet.
    this.dataAccessService.loadDepartments();
    this.dataAccessService.departments$.subscribe((departments) => (this.departments = departments));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entry'] && this.entry?.type === 'task') {
      this.loadComments();
    }
  }

  get task(): any {
    return this.entry?.source ?? null;
  }

  /** #4994: the task is held by a whole department (shown with the user-multiple icon). */
  get heldByDepartment(): boolean {
    return this.task?.assignedDepartmentId != null;
  }

  /** Who holds the task - department name or person (mirrors ProjectTasksComponent.holderName).
   *  Empty when nobody does; the template then falls back to the role name. */
  get assigneeDisplayName(): string | null {
    const task = this.task;
    if (task?.assignedDepartmentId != null)
      return task.assignedDepartmentName ?? this.departmentName(task.assignedDepartmentId);
    const email = task?.assignedUserEmail;
    return email ? this.userMap[email] || email : null;
  }

  /** Fallback shown greyed when nobody holds the task: the task's role (departmentId). */
  get roleName(): string | null {
    return this.departmentName(this.task?.departmentId);
  }

  private departmentName(id: number | null | undefined): string | null {
    if (id == null) return null;
    return this.departments.find((department) => department.id == id)?.name ?? null;
  }

  private loadComments(): void {
    if (!this.task?.id) {
      this.comments = [];
      return;
    }
    this.loadingComments = true;
    const payload = {
      CommentItem: {
        GridConnectionId: this.siteId,
        ProjectId: this.projectId,
        TaskId: this.task.id,
        CommentData: [],
      },
    };
    this.commentService.getComments(payload).then(
      (result) => {
        this.loadingComments = false;
        if (result?.hasError === true) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.comments = result?.comments?.commentData ?? [];
        }
      },
      (error: any) => {
        this.loadingComments = false;
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }
}
